/**
 * Shower toilets: the client side of server modules/toilets.ts and the iOS Bluetooth bridge (shared/toilet.ts).
 *
 * - Server: GET /api/homes/:home/toilets (on first use, every 30 s, on toilet.* bridge events and when the window
 *   regains focus) and the socket's `{t:'toilets', home, toilets}` push.
 * - Native (iOS app only): `toilet.configure` with the home's controllable toilets when the app opens a home (and
 *   again when their pairing changes), `toilet.links` when it comes back to the foreground; `toilet.state` events
 *   are kept live here and relayed to POST …/state (throttled per toilet: news at once, the rest at most every
 *   15 s); `toilet.paired` (and toilet.pair results) are saved with PUT …/pairing.
 */
import { statusMessage } from '../../lib/http-status.js';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type {
  BridgeEvent, BridgeInfo, ServerMsg, ToiletCommand, ToiletConfig, ToiletEntry, ToiletLinkState, ToiletPairing,
  ToiletDebug, ToiletPairResult, ToiletsResponse, ToiletState,
} from '@home/shared';
import { store } from '../../lib/store.js';
import { isNative, native, nativeCall, onNativeEvent } from '../../lib/native.js';
import { relayDecision } from './model.js';

const base = (home: string): string => `/api/homes/${encodeURIComponent(home)}/toilets`;

// ---------------------------------------------------------------- state

const lists = new Map<string, ToiletEntry[]>();
const live = new Map<string, ToiletState>();
const links = new Map<string, ToiletLinkState>();
const listeners = new Set<() => void>();
let version = 0;
const emit = (): void => {
  version++;
  listeners.forEach((fn) => fn());
};
const subscribe = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};
const EMPTY: ToiletEntry[] = [];

function put(home: string, toilets: ToiletEntry[]): void {
  lists.set(home, toilets);
  emit();
  void maybeConfigure(home);
}

const inflight = new Map<string, Promise<void>>();
/** Re-fetch a home's toilets (deduped). A 404 (no module / no access) means none; network errors keep the list. */
export function refreshToilets(home: string): Promise<void> {
  const cur = inflight.get(home);
  if (cur) return cur;
  const p = fetch(base(home), { credentials: 'same-origin' })
    .then(async (r) => {
      if (r.ok) put(home, ((await r.json()) as ToiletsResponse).toilets ?? []);
      else if (r.status === 404 || r.status === 403) put(home, []);
    })
    .catch(() => undefined)
    .finally(() => inflight.delete(home));
  inflight.set(home, p);
  return p;
}

let socketHooked = false;
function hookSocket(): void {
  if (socketHooked) return;
  socketHooked = true;
  store.onServerMessage((m: ServerMsg) => {
    if (m.t === 'toilets' && Array.isArray(m.toilets)) put(m.home, m.toilets);
  });
}

// Shared poller: one 30 s timer per home however many components watch it.
const watchers = new Map<string, { n: number; stop: () => void }>();
function watch(home: string): () => void {
  hookSocket();
  let w = watchers.get(home);
  if (!w) {
    void refreshToilets(home);
    const tick = (): void => void refreshToilets(home);
    const id = window.setInterval(tick, 30_000);
    window.addEventListener('focus', tick);
    w = { n: 0, stop: () => { window.clearInterval(id); window.removeEventListener('focus', tick); } };
    watchers.set(home, w);
  }
  w.n++;
  return () => {
    const cur = watchers.get(home);
    if (!cur) return;
    if (--cur.n <= 0) {
      cur.stop();
      watchers.delete(home);
    }
  };
}

/** The home's toilets (server view; empty until loaded or when it has none). */
export function useToilets(home: string): ToiletEntry[] {
  useEffect(() => watch(home), [home]);
  return useSyncExternalStore(subscribe, () => lists.get(home) ?? EMPTY);
}

/** Live phone-side state and link for one toilet (re-renders on any toilet change). */
export function useToiletLive(id: string): { live: ToiletState | undefined; link: ToiletLinkState | undefined } {
  useSyncExternalStore(subscribe, () => version);
  return { live: live.get(id), link: links.get(id) };
}

// ---------------------------------------------------------------- server writes

async function send(method: string, url: string, body: unknown): Promise<unknown> {
  const r = await fetch(url, { method, credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const text = await r.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!r.ok) {
    const msg = data && typeof data === 'object' && typeof (data as { message?: unknown }).message === 'string' ? (data as { message: string }).message : statusMessage(r.status);
    throw new Error(msg);
  }
  return data;
}

const homeOf = (toilet: string): string | undefined => {
  for (const [home, l] of lists) if (l.some((t) => t.id === toilet)) return home;
  return undefined;
};

let deviceName: Promise<string> | null = null;
function device(): Promise<string> {
  deviceName ??= native.info().then((i: BridgeInfo | null) => i?.deviceName || 'iPhone').catch(() => 'iPhone');
  return deviceName;
}

// ---------------------------------------------------------------- GROHE connect on demand

/** GROHE: ask the app to advertise for this toilet (the Connect button) or stop. Older apps (advertise whenever open)
 *  reject the command: ignored. */
export function activateToilet(toilet: string, on: boolean): Promise<void> {
  return nativeCall<unknown>({ cmd: 'toilet.activate', toilet, on }, 4000).then(() => undefined, () => undefined);
}

/** Duravit pairing fallback: the button was pressed but the app saw no advert change (iOS ≥ 1.5.4; older apps reject). */
export function confirmPairing(toilet: string): Promise<void> {
  return nativeCall<unknown>({ cmd: 'toilet.pairConfirm', toilet }, 4000).then(() => undefined, () => undefined);
}

// ---------------------------------------------------------------- bring-up traces

const TRACE_MS = 3000;

/**
 * The native Bluetooth trace for one toilet (toilet.debug, iOS ≥ 1.5.1), polled every TRACE_MS while `active`, and
 * relayed to the server (POST …/debug; it keeps only new lines and logs them) so bring-up can be followed remotely.
 * Older apps reject the command: polling stops.
 */
export function useToiletTrace(home: string, id: string, active: boolean): string[] {
  const [lines, setLines] = useState<string[]>([]);
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let lastSent = '';
    const poll = async (): Promise<void> => {
      let r: ToiletDebug | null = null;
      try { r = await nativeCall<ToiletDebug>({ cmd: 'toilet.debug', toilet: id }, 4000); } catch { stopped = true; return; }
      if (stopped || !r || !Array.isArray(r.lines)) return;
      setLines(r.lines);
      const tail = r.lines.slice(-200);
      if (!tail.length || tail.at(-1) === lastSent) return;
      lastSent = tail.at(-1)!;
      void send('POST', `${base(home)}/${encodeURIComponent(id)}/debug`, { lines: tail, device: await device() }).catch(() => undefined);
    };
    void poll();
    const t = window.setInterval(() => { if (!stopped) void poll(); }, TRACE_MS);
    return () => { stopped = true; window.clearInterval(t); };
  }, [home, id, active]);
  return lines;
}

const sent = new Map<string, { at: number; state: ToiletState }>();
const trailing = new Map<string, { timer: number; state: ToiletState }>();

/** Relay what the phone heard to the server, throttled per toilet (model.relayDecision). */
export function relayState(state: ToiletState, nowMs = Date.now()): void {
  const d = relayDecision(sent.get(state.id), state, nowMs);
  const pending = trailing.get(state.id);
  if (d === 'now') {
    if (pending) window.clearTimeout(pending.timer);
    trailing.delete(state.id);
    void postState(state);
    return;
  }
  if (pending) {
    pending.state = state;
    return;
  }
  const timer = window.setTimeout(() => {
    const p = trailing.get(state.id);
    trailing.delete(state.id);
    if (p) void postState(p.state);
  }, d);
  trailing.set(state.id, { timer, state });
}

async function postState(state: ToiletState): Promise<void> {
  const home = homeOf(state.id);
  if (!home) return;
  sent.set(state.id, { at: Date.now(), state });
  try {
    await send('POST', `${base(home)}/${encodeURIComponent(state.id)}/state`, { state, device: await device() });
  } catch {
    // The next frame retries; a resident's other phone may report too.
    sent.delete(state.id);
  }
}

const savedPairings = new Map<string, string>();
/** PUT the pairing a phone learnt (deduped: the pair call's result and the toilet.paired event carry the same). */
export async function savePairing(result: ToiletPairResult, home = homeOf(result.id)): Promise<void> {
  if (!home) return;
  const sig = JSON.stringify(result.pairing);
  if (savedPairings.get(result.id) === sig) return;
  savedPairings.set(result.id, sig);
  const pairing: ToiletPairing = { ...result.pairing, at: result.pairing.at ?? Math.floor(Date.now() / 1000), by: result.pairing.by ?? store.user?.username };
  try {
    await send('PUT', `${base(home)}/${encodeURIComponent(result.id)}/pairing`, { pairing });
  } catch (e) {
    savedPairings.delete(result.id);
    throw e;
  }
  await refreshToilets(home);
}

// ---------------------------------------------------------------- native bridge

/** Send a command to a toilet through the phone. Resolves false when the app didn't answer in time. */
export async function toiletCommand(toilet: string, command: ToiletCommand, timeoutMs = 8000): Promise<boolean> {
  // nativeCall resolves null both for a plain "done" and on timeout: tell them apart by the time it took.
  const t0 = Date.now();
  const r = await nativeCall<unknown>({ cmd: 'toilet.command', toilet, command }, timeoutMs);
  return r !== null || Date.now() - t0 < timeoutMs - 50;
}

/** Pair a toilet (the user presses the button on the toilet; up to ~90 s), then save the result for every phone. */
export async function pairToilet(home: string, toilet: string): Promise<ToiletPairResult | null> {
  const r = await nativeCall<ToiletPairResult>({ cmd: 'toilet.pair', toilet }, 90_000);
  if (!r) return null;
  await savePairing(r, home);
  return r;
}

const configured = new Map<string, string>();
const configFor = (t: ToiletEntry): ToiletConfig => ({ id: t.id, homeId: t.homeId, roomId: t.roomId, name: t.name, brand: t.brand, protocol: t.protocol, pairing: t.pairing });

/** Hand the native side this home's controllable toilets (only when the active home's set or pairing changed). */
async function maybeConfigure(home: string): Promise<void> {
  if (!isNative() || activeHome !== home || !lists.has(home)) return;
  const toilets = (lists.get(home) ?? []).filter((t) => t.canControl).map(configFor);
  const sig = JSON.stringify(toilets);
  if (configured.get(home) === sig) return;
  configured.clear();
  configured.set(home, sig);
  try {
    const r = await nativeCall<ToiletLinkState[]>({ cmd: 'toilet.configure', toilets }, 8000);
    if (Array.isArray(r)) setLinks(r);
  } catch {
    // Older app (unknown command): no Bluetooth control from this phone.
  }
}

function setLinks(list: ToiletLinkState[]): void {
  for (const l of list) if (l && typeof l.id === 'string') links.set(l.id, l);
  emit();
}

let refetchTimer: number | undefined;
function refetchSoon(): void {
  window.clearTimeout(refetchTimer);
  refetchTimer = window.setTimeout(() => { if (activeHome) void refreshToilets(activeHome); }, 1500);
}

function onEvent(e: BridgeEvent): void {
  switch (e.type) {
    case 'toilet.state':
      live.set(e.state.id, e.state);
      emit();
      relayState(e.state);
      break;
    case 'toilet.link':
      links.set(e.link.id, e.link);
      emit();
      if (e.link.link === 'connected') refetchSoon();
      break;
    case 'toilet.paired':
      void savePairing(e.result).catch(() => undefined);
      break;
    case 'app.foreground':
      if (activeHome) {
        void refreshToilets(activeHome);
        void nativeCall<ToiletLinkState[]>({ cmd: 'toilet.links' }, 4000).then((r) => Array.isArray(r) && setLinks(r)).catch(() => undefined);
      }
      break;
    default:
      break;
  }
}

let activeHome: string | null = null;
let unhook: (() => void) | null = null;

/** HomeLayout: in the iOS app, keep the open home's toilets configured on the phone and relay what it hears. */
export function useToiletBridge(home: string): void {
  const run = useCallback(() => {
    if (!home || !isNative()) return undefined;
    activeHome = home;
    unhook ??= onNativeEvent(onEvent);
    const stop = watch(home);
    void maybeConfigure(home);
    return () => {
      stop();
      if (activeHome === home) activeHome = null;
    };
  }, [home]);
  useEffect(run, [run]);
}

/** Test hook (unit tests): forget everything. */
export function resetToiletsData(): void {
  lists.clear(); live.clear(); links.clear(); sent.clear(); savedPairings.clear(); configured.clear();
  for (const t of trailing.values()) window.clearTimeout(t.timer);
  trailing.clear();
  emit();
}

// ---------------------------------------------------------------- admin

export interface ToiletAdminInput { id?: string; name?: string; roomId?: string; brand?: ToiletConfig['brand']; protocol?: ToiletConfig['protocol'] }
const adminBase = (home: string): string => `/api/homes/${encodeURIComponent(home)}/admin/toilets`;
export const toiletAdmin = {
  create: (home: string, body: ToiletAdminInput) => send('POST', adminBase(home), body).then(() => refreshToilets(home)),
  update: (home: string, id: string, body: ToiletAdminInput) => send('PATCH', `${adminBase(home)}/${encodeURIComponent(id)}`, body).then(() => refreshToilets(home)),
  remove: async (home: string, id: string) => {
    const r = await fetch(`${adminBase(home)}/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'same-origin' });
    if (!r.ok) throw new Error(`Delete failed (${r.status})`);
    await refreshToilets(home);
  },
};
export const PROTOCOL_FOR = { grohe: 'lixil', duravit: 'sensowash-serial' } as const;
